'use client';

import React, { useEffect, useState } from 'react';
import RichTextRenderer from 'ui/components/RichTextRenderer/RichTextRenderer';
import clsx from 'clsx';
import {
  Accordion,
  AccordionItem,
  CustomAccordionTrigger,
  AccordionContent,
} from 'ui/components/AccordionV3/Accordion';
import { Add } from 'ui/icons/icons/Actions/Add';
import { Remove } from 'ui/icons/icons/Actions/Remove';
import { useTranslation } from '@whoop/i18n/lang/client';
import type { CollapsibleInfoSubsection } from '@/lib/constants/maverick';
import SectionHeader from '@/components/SectionHeader/SectionHeader';

interface OplCollapsibleSectionProps {
  collapsibleSubsections: CollapsibleInfoSubsection[];
}

export default function OplCollapsibleSection({
  collapsibleSubsections,
}: OplCollapsibleSectionProps): React.JSX.Element | null {
  const { t } = useTranslation('membershipTiers');

  const hasSections = collapsibleSubsections.length > 0;
  const defaultSection = hasSections ? collapsibleSubsections[0] : null;
  const [currentSection, setCurrentSection] =
    useState<CollapsibleInfoSubsection | null>(defaultSection);
  const [currentImage, setCurrentImage] = useState<string>(
    defaultSection?.image.url ?? '',
  );
  const [prevImage, setPrevImage] = useState<string>('');
  const [imageFade, setImageFade] = useState(false);
  const [accordionActiveValue, setAccordionActiveValue] = useState<string>(
    defaultSection ? defaultSection.heading : '',
  );

  const findSectionByHeading = (heading: string) => {
    return (
      collapsibleSubsections.find((section) => section.heading === heading) ||
      defaultSection
    );
  };

  useEffect(() => {
    if (accordionActiveValue) {
      setCurrentSection(findSectionByHeading(accordionActiveValue));
    }
  }, [accordionActiveValue]);

  useEffect(() => {
    if (currentSection?.heading !== accordionActiveValue) {
      setPrevImage(currentImage);
      setImageFade(true);

      setTimeout(() => {
        setCurrentImage(
          findSectionByHeading(accordionActiveValue)?.image.url ?? '',
        );
        setImageFade(false);
      }, 150);
    }
  }, [currentSection, accordionActiveValue]);

  return hasSections ? (
    <div className='flex w-full flex-col items-center justify-center bg-gray-50 py-14'>
      <div className='w-[88%] lg:max-w-screen-lg'>
        <SectionHeader className='flex w-[70%]'>
          {t('wearWhoopYourWay')}
        </SectionHeader>
        <div className='flex w-full flex-col-reverse pt-6 md:flex-row md:pt-8'>
          <div className='relative flex w-full flex-col pt-8 md:w-[40%] md:min-w-[452px] md:pb-0'>
            {collapsibleSubsections.map((section) => (
              <div
                className='mb-8 flex flex-row items-center'
                key={section.heading}
              >
                <div
                  className={clsx(
                    'mr-4 w-[3px] flex-shrink-0 self-stretch rounded-md bg-black',
                    accordionActiveValue === section.heading
                      ? 'opacity-100'
                      : 'opacity-40',
                  )}
                />
                <Accordion
                  className='w-full'
                  collapsible
                  onValueChange={setAccordionActiveValue}
                  type='single'
                  value={accordionActiveValue}
                >
                  <AccordionItem value={section.heading}>
                    <CustomAccordionTrigger
                      className='flex items-start p-0 text-start'
                      icon={
                        <div className='icon-container flex w-6 items-center justify-center lg:w-8'>
                          {accordionActiveValue === section.heading ? (
                            <Remove fill='black' title='Close' width={20} />
                          ) : (
                            <Add fill='gray' title='Open' width={20} />
                          )}
                        </div>
                      }
                    >
                      <h3
                        className={clsx(
                          'text-md w-[80%] font-bold md:w-full md:whitespace-nowrap md:text-2xl',
                          accordionActiveValue !== section.heading
                            ? 'opacity-40'
                            : 'mb-2 md:mb-6',
                        )}
                      >
                        {section.heading}
                      </h3>
                    </CustomAccordionTrigger>
                    <AccordionContent>
                      <div>
                        <RichTextRenderer content={section.bodyText.json} />
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            ))}
          </div>
          <div className='relative flex w-full items-start justify-start md:min-h-[340px] md:justify-end md:pl-6'>
            <div className='relative h-full w-full rounded-3xl md:w-[90%] md:max-w-[600px] md:overflow-hidden'>
              {/* {prevImage ? (
                <img
                  alt={prevImage}
                  className={clsx(
                    'absolute top-0 h-full w-full rounded-3xl object-cover transition-opacity duration-150 md:left-0',
                  )}
                  src={prevImage}
                />
              ) : null} */}
              {currentImage ? (
                <img
                  className={clsx(
                    'h-full w-full rounded-3xl object-cover transition-opacity duration-150 md:absolute md:left-0 md:top-0',
                    imageFade ? 'opacity-0' : 'opacity-100',
                  )}
                  src={currentImage}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : null;
}
